import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Send, Users, Clock, TrendingUp, MailWarning, Eye, MousePointerClick } from "lucide-react";
import ThinkingLoader from "@/components/ThinkingLoader";

/**
 * Admin widget showing per-affiliate invite activity: who's actually
 * sending invites and driving traffic vs. who's opted-in but dormant.
 *
 * Consumes GET /api/admin/affiliate/invites/stats and renders four
 * headline metrics + a Top Senders table. When Resend webhook events
 * land in a future iteration this widget will grow open/click columns
 * without changing shape.
 */
export default function AdminInviteTrackingWidget() {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/affiliate/invites/stats?days=${d}`);
      setData(r.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(days); }, [load, days]);

  const totals = data?.totals || {};
  const senders = data?.top_senders || [];

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("nl-NL", { day: "2-digit", month: "short" });
    } catch { return "—"; }
  };

  return (
    <div className="bg-white border border-black/10 p-6 sm:p-8" data-testid="admin-invite-tracking-widget">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <p className="ed-eyebrow">{t("admInvitesEyebrow")}</p>
          <h3 className="font-serif text-2xl mt-2 leading-tight">{t("admInvitesTitle")}</h3>
          <p className="text-sm text-neutral-600 mt-2 max-w-lg leading-relaxed">
            {t("admInvitesBody")}
          </p>
        </div>
        <div className="flex border border-neutral-300 w-fit" data-testid="admin-invites-window">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`admin-invites-window-${d}`}
              onClick={() => setDays(d)}
              className={`px-3 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${
                days === d ? "bg-neutral-900 text-[#F9F8F6]" : "hover:bg-neutral-100"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Headline metrics — 6 stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {[
          { Icon: Send, label: t("admInvitesStatSends"), value: totals.sends ?? 0, testid: "sends" },
          { Icon: Eye, label: t("admInvitesStatOpens"), value: totals.opens ?? 0, testid: "opens", pct: totals.open_rate_pct },
          { Icon: MousePointerClick, label: t("admInvitesStatClicks"), value: totals.clicks ?? 0, testid: "clicks", pct: totals.click_rate_pct },
          { Icon: Users, label: t("admInvitesStatRecipients"), value: totals.unique_recipients ?? 0, testid: "recipients" },
          { Icon: TrendingUp, label: t("admInvitesStatActive"), value: totals.active_senders ?? 0, testid: "active" },
          { Icon: MailWarning, label: t("admInvitesStatDormant"), value: totals.dormant_affiliates ?? 0, testid: "dormant", warn: true },
        ].map(({ Icon, label, value, testid, warn, pct }) => (
          <div
            key={testid}
            data-testid={`admin-invites-metric-${testid}`}
            className={`border p-4 ${warn && value > 0 ? "border-amber-300 bg-amber-50" : "border-neutral-200 bg-neutral-50"}`}
          >
            <div className="flex items-center gap-2">
              <Icon size={12} strokeWidth={1.75} className={warn && value > 0 ? "text-amber-700" : "text-neutral-500"} />
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-600">{label}</span>
            </div>
            <div className="flex items-baseline gap-1.5 mt-2">
              <p className={`font-serif text-3xl tabular-nums ${warn && value > 0 ? "text-amber-800" : "text-neutral-900"}`}>
                {value}
              </p>
              {typeof pct === "number" && pct > 0 && (
                <span className="font-mono text-[0.65rem] text-neutral-500 tabular-nums">
                  {pct}%
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Top senders table */}
      <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 mb-3">
        {t("admInvitesTopSenders")}
      </p>
      {loading ? (
        <ThinkingLoader stage="generic" testId="admin-invites-thinking" />
      ) : senders.length === 0 ? (
        <div data-testid="admin-invites-empty" className="border border-dashed border-neutral-300 p-6 text-center">
          <p className="text-sm text-neutral-600">{t("admInvitesEmpty")}</p>
          <p className="font-mono text-[0.6rem] text-neutral-500 mt-2">
            {t("admInvitesEmptyHint", { total: totals.total_affiliates ?? 0 })}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-invites-table">
            <thead>
              <tr className="border-b border-neutral-300">
                <th className="text-left font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 py-2">
                  {t("admInvitesColAffiliate")}
                </th>
                <th className="text-right font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 py-2">
                  {t("admInvitesColSends")}
                </th>
                <th className="text-right font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 py-2 hidden md:table-cell">
                  {t("admInvitesColOpens")}
                </th>
                <th className="text-right font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 py-2 hidden md:table-cell">
                  {t("admInvitesColClicks")}
                </th>
                <th className="text-right font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 py-2">
                  {t("admInvitesColConversions")}
                </th>
                <th className="text-right font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 py-2 hidden sm:table-cell">
                  {t("admInvitesColLastSent")}
                </th>
              </tr>
            </thead>
            <tbody>
              {senders.map((s, i) => {
                const conv = s.conversions_in_window || 0;
                const conversionRate = s.sends > 0 ? Math.round((conv / s.sends) * 100) : 0;
                const openRate = s.sends > 0 ? Math.round((s.opens / s.sends) * 100) : 0;
                const clickRate = s.sends > 0 ? Math.round((s.clicks / s.sends) * 100) : 0;
                return (
                  <tr key={s.affiliate_user_id} data-testid={`admin-invites-row-${i}`} className="border-b border-neutral-100">
                    <td className="py-3">
                      <p className="font-serif text-base leading-tight">{s.name}</p>
                      <p className="font-mono text-[0.6rem] text-neutral-500">{s.email}</p>
                    </td>
                    <td className="py-3 text-right font-mono text-sm tabular-nums">{s.sends}</td>
                    <td className="py-3 text-right font-mono text-sm text-neutral-700 tabular-nums hidden md:table-cell">
                      <span className={s.opens > 0 ? "" : "text-neutral-400"}>{s.opens || 0}</span>
                      {s.opens > 0 && <span className="ml-1 text-[0.65rem] text-neutral-500">({openRate}%)</span>}
                    </td>
                    <td className="py-3 text-right font-mono text-sm text-neutral-700 tabular-nums hidden md:table-cell">
                      <span className={s.clicks > 0 ? "text-blue-700 font-semibold" : "text-neutral-400"}>{s.clicks || 0}</span>
                      {s.clicks > 0 && <span className="ml-1 text-[0.65rem] text-neutral-500">({clickRate}%)</span>}
                    </td>
                    <td className="py-3 text-right font-mono text-sm tabular-nums">
                      <span className={conv > 0 ? "text-emerald-700 font-semibold" : "text-neutral-400"}>
                        {conv}
                      </span>
                      {conv > 0 && (
                        <span className="ml-1 text-[0.65rem] text-neutral-500">
                          ({conversionRate}%)
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-right font-mono text-[0.65rem] text-neutral-500 hidden sm:table-cell">
                      <Clock size={10} strokeWidth={1.5} className="inline mr-1 align-middle" />
                      {fmtDate(s.last_sent_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
